import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { IonIcon } from '@ionic/react';
import { IonicButton } from 'ionic-ui';
import {
  arrowDownOutline,
  arrowUpOutline,
  receiptOutline,
} from 'ionicons/icons';
import clsx from 'clsx';
import DialogTitle from '../../../../DialogTitle';
import { RootState } from '../../../../../../store/store';
import { formatPrice } from '../../../../../../utils/formatPrice';
import {
  getWalletTransactions,
  WalletTransaction,
} from '../../../../../../services/walletSetup/walletOps/walletOps';

const PAGE_SIZE = 10;

const statusStyle: Record<WalletTransaction['status'], string> = {
  successful: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  reversed: 'bg-gray-100 text-gray-600',
};

const typeLabel: Record<WalletTransaction['type'], string> = {
  topup: 'Top up',
  wallet_to_wallet: 'Wallet transfer',
  payout_external: 'Withdrawal',
};

const WalletTransactionsDialog: React.FC = () => {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['wallet-transactions', page],
    queryFn: () =>
      getWalletTransactions({
        authToken: authToken!,
        page,
        page_size: PAGE_SIZE,
      }),
    enabled: Boolean(authToken),
  });

  const records = data?.records || [];
  const hasNextPage = records.length === PAGE_SIZE;

  return (
    <div className="space-y-4 sm:max-w-[480px] dialog-content">
      <DialogTitle>Wallet transactions</DialogTitle>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-14 rounded-lg bg-black/5 dark:bg-white/5 animate-pulse"
            />
          ))}
        </div>
      )}

      {isError && (
        <p className="text-sm text-red-500">
          Could not load transactions. Please try again.
        </p>
      )}

      {!isLoading && !isError && records.length === 0 && (
        <div className="text-center py-8 space-y-2">
          <IonIcon
            icon={receiptOutline}
            className="size-10 medium-emphasis-text mx-auto"
          />
          <p className="text-sm medium-emphasis-text">No transactions yet.</p>
        </div>
      )}

      <div className="divide-y divide-border">
        {records.map((tx) => {
          const isCredit = tx.type === 'topup';
          return (
            <div key={tx.id} className="flex items-center gap-3 py-3">
              <div
                className={clsx(
                  'size-9 rounded-full flex items-center justify-center shrink-0',
                  isCredit
                    ? 'bg-green-100 text-green-600'
                    : 'bg-red-100 text-red-600'
                )}
              >
                <IonIcon
                  icon={isCredit ? arrowDownOutline : arrowUpOutline}
                  className="size-4"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium high-emphasis-text truncate">
                  {typeLabel[tx.type] || tx.type}
                </p>
                <p className="text-xs medium-emphasis-text truncate">
                  {tx.remarks || tx.reference}
                </p>
                <p className="text-[11px] medium-emphasis-text">
                  {new Date(tx.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p
                  className={clsx(
                    'text-sm font-semibold',
                    isCredit ? 'text-green-600' : 'high-emphasis-text'
                  )}
                >
                  {isCredit ? '+' : '-'}
                  {formatPrice(Number(tx.amount))}
                </p>
                <span
                  className={clsx(
                    'inline-block text-[10px] font-medium px-2 py-0.5 rounded-full capitalize',
                    statusStyle[tx.status] || 'bg-gray-100 text-gray-600'
                  )}
                >
                  {tx.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {(page > 1 || hasNextPage) && (
        <div className="flex items-center justify-between">
          <IonicButton
            variant="outline"
            type="button"
            disabled={page === 1 || isLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </IonicButton>
          <span className="text-xs medium-emphasis-text">Page {page}</span>
          <IonicButton
            variant="outline"
            type="button"
            disabled={!hasNextPage || isLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </IonicButton>
        </div>
      )}
    </div>
  );
};

export default WalletTransactionsDialog;
